"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.InvoiceFeeRecordSchema = exports.InvoiceFeeLineSchema = void 0;
exports.invoiceNetCents = invoiceNetCents;
const zod_1 = require("zod");
/**
 * PLATFORM PROCESSING FEES — every cent Stripe takes, recorded.
 *
 * ---------------------------------------------------------------------------
 * THE POLICY THIS ENCODES
 * ---------------------------------------------------------------------------
 *
 * RubyTill ABSORBS the Stripe fee as its own operating expense. It is not
 * rebilled to a tenant, a partner or an ISO, and it is not deducted from a
 * partner's commission — a partner's percentage is computed on the GROSS
 * amount, exactly as their agreement says. The fee only ever reduces OUR net.
 *
 * That makes it an expense line, and an expense line has to be recorded to the
 * cent or the P&L is fiction. Before this, nothing in the platform recorded it
 * at all.
 *
 * ---------------------------------------------------------------------------
 * WHY A WHOLE-INVOICE RECORD, NOT JUST PER-DEVICE
 * ---------------------------------------------------------------------------
 *
 * The first cut of this attributed a fee share only to DEVICE-FINANCE invoice
 * lines, because those are the lines that resolve to a unit. Every other line —
 * software subscription, tax, proration, add-ons — had its share computed and
 * then DISCARDED, because there was no unit to hang it on. On a typical invoice
 * that silently dropped the majority of the fee.
 *
 * So the invoice-level record below is the authority for "what did Stripe take
 * from us", and the per-unit `feeCents` on a payment row is a derived
 * ATTRIBUTION of part of it, for per-device margin. The two must never be
 * summed together — that would double-count. Report OUR total fee expense from
 * these records, and only these.
 */
/** One invoice line's share of the fee, kept so a report can attribute without re-deriving. */
exports.InvoiceFeeLineSchema = zod_1.z.object({
    /** Stripe price id for the line. Absent on lines with no price (some tax lines). */
    priceId: zod_1.z.string().optional(),
    /** This line's gross amount in integer cents. */
    amountCents: zod_1.z.number().int(),
    /** This line's apportioned share of `feeCents`. Shares sum EXACTLY to the total. */
    feeCents: zod_1.z.number().int(),
    /** Whether this line was attributed to a financed device unit. */
    attributedToUnits: zod_1.z.boolean().default(false),
});
exports.InvoiceFeeRecordSchema = zod_1.z.object({
    /** Partition key on the invoices table. */
    tenantId: zod_1.z.string().min(1),
    /** Sort key. Derived from the Stripe invoice id so a redelivery overwrites rather than duplicates. */
    invoiceId: zod_1.z.string().min(1),
    stripeInvoiceId: zod_1.z.string().min(1),
    stripeChargeId: zod_1.z.string().optional(),
    /** What the customer paid, integer cents. */
    grossCents: zod_1.z.number().int().min(0),
    /**
     * What Stripe took, integer cents, from the charge's balance transaction.
     *
     * ABSENT means "not yet known" and NEVER zero — Stripe settles balance
     * transactions asynchronously, so the fee is frequently unavailable at
     * `invoice.paid` time. A report must render absent as unknown; treating it
     * as 0 reasserts the exact overstatement this record exists to end. A
     * genuinely zero fee (100% coupon, $0 invoice) is stored AS 0.
     */
    feeCents: zod_1.z.number().int().min(0).optional(),
    /** grossCents - feeCents. Absent whenever feeCents is absent. */
    netCents: zod_1.z.number().int().optional(),
    /** Stripe's own currency code, lower case, as returned. */
    currency: zod_1.z.string().default('usd'),
    /** Per-line apportionment. Empty when the fee was unknown at write time. */
    lines: zod_1.z.array(exports.InvoiceFeeLineSchema).default([]),
    /**
     * Set when the fee was still unknown at write time, so a later backfill job
     * can find exactly these rows instead of scanning everything.
     */
    feePending: zod_1.z.boolean().optional(),
    paidAt: zod_1.z.number(),
    createdAt: zod_1.z.number(),
    updatedAt: zod_1.z.number(),
});
/**
 * NOTE ON APPORTIONMENT: this module deliberately does NOT define its own.
 * `apportionCents(weights, amountCents)` in `planAssignment.ts` already splits
 * an amount across weights in whole cents with an exact-sum guarantee, and is
 * already tested. A second copy of money arithmetic is exactly the kind of
 * drift that makes two reports disagree, so the fee path uses that one.
 */
/** Our net on an invoice. Absent fee gives an absent net — never `gross - 0`. */
function invoiceNetCents(grossCents, feeCents) {
    if (typeof feeCents !== 'number' || !Number.isFinite(feeCents))
        return undefined;
    return Math.trunc(grossCents) - Math.trunc(feeCents);
}
//# sourceMappingURL=platformFees.js.map